import { CheckOutlined } from '@ant-design/icons';
import { useField } from 'formik';
import { nanoid } from 'nanoid';
import { type FC, useCallback, useMemo } from 'react';
import { type SelectFieldProps } from '../../../components/fields/SelectField';
import { type Brightness } from '../../context';
import SelectContainer, { StyledPorscheArrow } from './shared';

type PorscheSelectFieldProps = Omit<SelectFieldProps, 'options'> & Brightness;

const withPorscheSelect = (SelectField: FC<PorscheSelectFieldProps>) =>
    function PorscheSelectField({ name, brightness = 'light', ...props }: PorscheSelectFieldProps) {
        const [field] = useField({ name });
        const enhanceOptions = useCallback<SelectFieldProps['enhanceOptions']>(
            options =>
                (options || []).map(option => ({
                    ...option,
                    selectLabel: option.label,
                    label: (
                        <>
                            {option.label} {field.value === option.value ? <CheckOutlined role="img" /> : null}
                        </>
                    ),
                })),
            [field.value]
        );

        const id = useMemo(() => nanoid(), []);

        const filterOption = (input: string, option) =>
            (option?.selectLabel ?? '').toLowerCase().includes(input.toLowerCase());

        return (
            <SelectContainer brightness={brightness} id={id}>
                <SelectField
                    name={name}
                    {...props}
                    brightness={brightness}
                    enhanceOptions={enhanceOptions}
                    filterOption={filterOption}
                    getPopupContainer={() => document.getElementById(id)}
                    optionLabelProp="selectLabel"
                    suffixIcon={<StyledPorscheArrow />}
                />
            </SelectContainer>
        );
    };

export default withPorscheSelect;
